<template>
    <div class="shadow-sm sm:overflow-hidden sm:rounded-md">
        <div class="space-y-6 bg-white px-4 py-4 sm:p-6">
            <div>
                <h3 class="text-base font-semibold text-gray-900">{{ $t('profile.portfolio_title') }}</h3>
                <p class="mt-1 text-sm text-gray-500">
                    {{ $t('profile.portfolio_tip') }}
                    <span class="text-gray-400">{{ $t('profile.portfolio_tip_2') }}</span>
                </p>
            </div>

            <div class="flex flex-col sm:flex-row items-center gap-2 justify-between">
                <div class="text-gray-500">
                    <p class="text-sm">{{ $t('profile.portfolio_groups.description') }}</p>
                </div>

                <div>
                    <button
                        type="button"
                        class="inline-flex items-center gap-1 rounded-md border border-royal-blue-600 text-royal-blue-600 px-2 py-1.5 text-xs hover:bg-royal-blue-600 cursor-pointer hover:text-white"
                        @click="beginAddGroup"
                    >
                        <PlusIcon class="size-4" />
                        <span>{{ $t('profile.portfolio_groups.add_button') }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>

    <draggable
        v-model="groupNamesArray"
        @end="onCategoryDragEnd"
        item-key="name"
        tag="div"
        handle=".category-drag-handle"
        :group="{ name: 'portfolio-categories', pull: false, put: false }"
        class="py-4 grid gap-6"
    >
        <template #item="{ element: groupName }">
            <section
                :key="groupName"
                class="rounded-md"
            >
                <header class="mb-3 flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <button
                            type="button"
                            class="category-drag-handle inline-flex items-center p-1 text-gray-400 hover:text-gray-600 cursor-move"
                        >
                            <Bars3Icon class="size-5" />
                        </button>
                        
                        <h4 class="font-semibold text-gray-900">{{ groupName }}</h4>

                        <button
                            type="button"
                            class="inline-flex items-center gap-x-1 rounded-md border-royal-blue-600 text-royal-blue-600 p-1 text-xs hover:bg-royal-blue-600 cursor-pointer hover:text-white"
                            @click="startRename(groupName)"
                        >
                            <PencilSquareIcon class="size-4" />
                            <span>Edit</span>
                        </button>
                    </div>

                    <span class="text-xs text-gray-500 inline-flex items-center gap-1">
                        <DocumentIcon class="size-3" />
                        {{ groupedLocal[groupName]?.length || 0 }}
                    </span>
                </header>

            <div v-if="Array.isArray(groupedLocal[groupName]) && groupedLocal[groupName].length === 0" class="bg-white shadow-sm rounded-lg p-6">
                <div class="text-center mb-6">
                    <p class="font-medium text-gray-800 mb-2">
                        {{ $t('profile.portfolio_groups.empty_group.title') }}
                    </p>
                    <p class="text-sm text-gray-500 mb-1">
                        {{ $t('profile.portfolio_groups.empty_group.description') }}
                    </p>
                    <p class="text-xs text-gray-400">
                        {{ $t('profile.portfolio_groups.empty_group.auto_delete') }}
                    </p>
                </div>
                
                <div class="max-w-xs mx-auto">
                    <div class="relative block w-full rounded-lg border-2 border-dashed border-gray-300 text-center hover:border-gray-400 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden" :class="{'p-3': galleriesToSelect.length, 'flex items-center justify-center min-h-[180px] px-3': !galleriesToSelect.length}">
                        <template v-if="galleriesToSelect.length">
                            <div class="mt-2 grid grid-cols-1">
                                <select
                                    v-model="addItemForms[groupName].gallery"
                                    class="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6">
                                    <option :value="null">{{ $t('profile.choose_file') }}</option>
                                    <option v-for="(gallery, index) in galleriesToSelect" :value="gallery" :key="`gallery-select-item-option-${index}-${gallery.id}`">{{ gallery.name }}</option>
                                </select>
                                <ChevronDownIcon class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" aria-hidden="true" />
                            </div>
                            <span class="my-2 block text-sm font-semibold text-gray-900">{{ $t('profile.or') }}</span>
                            <FileUploaderComponent :group="groupName" />
                        </template>
                        <template v-else>
                            <div class="w-full py-8 flex justify-center">
                                <div class="max-w-xs w-full">
                                    <FileUploaderComponent :group="groupName" />
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <draggable
                v-else-if="Array.isArray(groupedLocal[groupName])"
                :list="groupedLocal[groupName] || []"
                item-key="id"
                :group="{ name: 'profile-portfolio', pull: true, put: true }"
                @end="onDragEnd"
                tag="ul"
                class="grid grid-cols-2 gap-x-4 gap-y-8 bg-white shadow-sm rounded-lg p-3 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 xl:gap-x-4"
            >
                <template #item="{ element }">
                    <li v-if="element.id !== 'add'" class="relative cursor-pointer">
                        <div class="group h-full block max-w-50 sm:max-w-full w-full overflow-hidden rounded-lg bg-gray-100">
                            <img :src="element.cover_url" alt="" class="h-full group-hover:opacity-80 pointer-events-none aspect-10/7 object-cover" />
                            <button type="button" class="absolute inset-0 focus:outline-hidden cursor-pointer">
                                <span class="sr-only">{{ element.title }}</span>
                            </button>
                        </div>

                        <button
                            class="absolute top-2 right-2 text-white bg-red-600 p-1 rounded-full hover:bg-red-700 focus:outline-none cursor-pointer"
                            @click="removeItem(element)"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash2" viewBox="0 0 16 16">
                                <path d="M14 3a.702.702 0 0 1-.037.225l-1.684 10.104A2 2 0 0 1 10.305 15H5.694a2 2 0 0 1-1.973-1.671L2.037 3.225A.703.703 0 0 1 2 3c0-1.105 2.686-2 6-2s6 .895 6 2zM3.215 4.207l1.493 8.957a1 1 0 0 0 .986.836h4.612a1 1 0 0 0 .986-.836l1.493-8.957C11.69 4.689 9.954 5 8 5c-1.954 0-3.69-.311-4.785-.793z"/>
                            </svg>
                        </button>

                        <p class="absolute bottom-0 rounded-lg w-full p-1.5 left-0 text-white text-sm bg-gradient-to-t from-black/80 to-transparent">
                            {{ element.title }}
                        </p>
                    </li>
                </template>

                <template v-slot:footer>
                    <div class="relative block w-full rounded-lg border-2 border-dashed border-gray-300 text-center hover:border-gray-400 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden" :class="{'p-3': galleriesToSelect.length, 'flex items-center justify-center min-h-[180px] px-3': !galleriesToSelect.length}">
                        <template v-if="galleriesToSelect.length">
                            <div class="mt-2 grid grid-cols-1">
                                <select
                                    v-model="addItemForms[groupName].gallery"
                                    class="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6">
                                    <option :value="null">{{ $t('profile.choose_file') }}</option>
                                    <option v-for="(gallery, index) in galleriesToSelect" :value="gallery" :key="`gallery-select-item-option-${index}-${gallery.id}`">{{ gallery.name }}</option>
                                </select>
                                <ChevronDownIcon class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" aria-hidden="true" />
                            </div>
                            <span class="my-2 block text-sm font-semibold text-gray-900">{{ $t('profile.or') }}</span>
                            <FileUploaderComponent :group="groupName" />
                        </template>
                        <template v-else>
                            <div class="w-full py-8">
                                <FileUploaderComponent :group="groupName" />
                            </div>
                        </template>
                    </div>
                </template>
            </draggable>
            </section>
        </template>
    </draggable>

    <AddNewGroupToPortfolio :open="isAdding" @close="cancelAddGroup" @confirmAction="commitAddGroup" />
    <EditGroupInPortfolio :open="editingValue !== ''" :value="editingValue" @close="cancelRename(editingValue)" @confirmAction="applyRename" />

<!--    <draggable-->
<!--        v-model="sortedItems"-->
<!--        @start="drag=true"-->
<!--        @end="drag=false"-->
<!--        item-key="id"-->
<!--        tag="ul"-->
<!--        :class="{'grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 xl:gap-x-4 py-4': true}"-->
<!--    >-->
<!--        <template #item="{element}">-->
<!--            <li v-if="element.id !== 'add'" class="relative cursor-pointer">-->
<!--                <div :class="['group block w-full overflow-hidden rounded-lg bg-gray-100']">-->
<!--                    <img :src="element.cover_url" alt="" :class="['group-hover:opacity-80', 'pointer-events-none aspect-10/7 object-cover']" />-->
<!--                    <button type="button" class="absolute inset-0 focus:outline-hidden cursor-pointer">-->
<!--                        <span class="sr-only">{{ element.title }}</span>-->
<!--                    </button>-->
<!--                </div>-->

<!--                <button-->
<!--                    class="absolute top-2 right-2 text-white bg-red-600 p-1 rounded-full hover:bg-red-700 focus:outline-none cursor-pointer"-->
<!--                    @click="removeItem(element)"-->
<!--                >-->
<!--                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash2" viewBox="0 0 16 16">-->
<!--                        <path d="M14 3a.702.702 0 0 1-.037.225l-1.684 10.104A2 2 0 0 1 10.305 15H5.694a2 2 0 0 1-1.973-1.671L2.037 3.225A.703.703 0 0 1 2 3c0-1.105 2.686-2 6-2s6 .895 6 2zM3.215 4.207l1.493 8.957a1 1 0 0 0 .986.836h4.612a1 1 0 0 0 .986-.836l1.493-8.957C11.69 4.689 9.954 5 8 5c-1.954 0-3.69-.311-4.785-.793z"/>-->
<!--                    </svg>-->
<!--                </button>-->

<!--                <p class="absolute bottom-0 rounded-lg w-full p-1.5 left-0 text-white text-sm bg-gradient-to-t from-black/80 to-transparent">-->
<!--                    {{ element.title }}-->
<!--                </p>-->
<!--            </li>-->

<!--            <div v-else class="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-3 text-center hover:border-gray-400 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden">-->
<!--                <div class="mt-2 grid grid-cols-1" v-if="galleriesToSelect.length">-->
<!--                    <select-->
<!--                        v-model="addItemForm.gallery"-->
<!--                        class="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6">-->
<!--                        <option :value="null">{{ $t('profile.choose_file') }}</option>-->
<!--                        <option v-for="(gallery, index) in galleriesToSelect" :value="gallery" :key="`gallery-select-item-option-${index}-${gallery.id}`">{{ gallery.name }}</option>-->
<!--                    </select>-->
<!--                    <ChevronDownIcon class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" aria-hidden="true" />-->
<!--                </div>-->
<!--                <span v-if="galleriesToSelect.length" class="my-2 block text-sm font-semibold text-gray-900">{{ $t('profile.or') }}</span>-->
<!--                <FileUploaderComponent :class="{'py-10': !galleriesToSelect.length}" />-->
<!--            </div>-->
<!--        </template>-->
<!--    </draggable>-->
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {ChevronDownIcon, PlusIcon, PencilSquareIcon, Bars3Icon} from "@heroicons/vue/16/solid";
import {DocumentIcon} from "@heroicons/vue/24/outline";
import FileUploaderComponent from "@/components/Users/Profile/FileUploaderComponent.vue";
import draggable from "vuedraggable";
import {mapActions, mapGetters} from "vuex";
import {GalleryOption} from "@/types/GalleryType";
import {UserProfileItem} from "@/types/UserType";
import {validationMethods} from "@/_helpers/validation";
import {addGallery} from "@/data/requests/profiles";
import AddNewGroupToPortfolio from "@/components/Users/Profile/Modals/AddNewGroupToPortfolio.vue";
import EditGroupInPortfolio from "@/components/Users/Profile/Modals/EditGroupInPortfolio.vue";

type GroupMap = Record<string, UserProfileItem[]>;

export default defineComponent({
    name: "PortfolioSection",

    components: {
        EditGroupInPortfolio,
        AddNewGroupToPortfolio,
        FileUploaderComponent,
        draggable,
        ChevronDownIcon,

        PlusIcon,
        PencilSquareIcon,
        DocumentIcon,
        Bars3Icon
    },

    props: {
        //
    },

    data() {
        return {
            drag: false,

            addItemForms: {} as Record<string, {gallery: GalleryOption | null, file: File | null}>,

            groupedLocal: {} as GroupMap,

            editing: {} as Record<string, boolean>,
            editingValue: "",
            isAdding: false,
            newGroupName: "",
            isProcessingGalleryAdd: false,
        };
    },

    computed: {
        ...mapGetters({
            user: "user/user",

            profile: "profile/profile",
            profileItems: "profile/items",

            itemsGrouped: "profile/itemsGrouped",

            galleriesList: "galleries/galleriesList",
        }),

        galleriesToSelect: {
            get(): GalleryOption[] {
                if(this.galleriesList.length === 0) {
                    return [];
                }

                return this.galleriesList.filter((gallery: GalleryOption) => {
                    return !this.profileItems.some((item: UserProfileItem) => item.gallery_id === gallery.id);
                });
            },
            set(value: GalleryOption[]) {
                console.log(value);
            },
        },

        sortedItems: {
            get(): UserProfileItem[] {
                return [...this.profileItems, {
                    id: "add",
                    title: "Add",
                    cover_url: "https://via.placeholder.com/300",
                }];
            },
            set(value: UserProfileItem[]) {
                this.sortItems({
                    items: value.filter(item => item.id !== "add"),
                });
            },
        },

        groupNames(): string[] {
            return Object.keys(this.groupedLocal || {});
        },

        groupNamesArray: {
            get(): string[] {
                return this.groupNames;
            },
            set(value: string[]) {
                const newGrouped: GroupMap = {};
                for (const name of value) {
                    newGrouped[name] = this.groupedLocal[name] || [];
                }
                this.groupedLocal = newGrouped;
            }
        },

        dragGroup(): any {
            return { name: "profile-portfolio", pull: true, put: true };
        },
    },

    mounted() {
        this.fetchGalleries();

        this.loadProfileItems()
            .then((items) => {
                console.log('fetch items', items);
            });
    },

    watch: {
        itemsGrouped: {
            deep: true,
            immediate: true,
            handler(val: Record<string, UserProfileItem[]>) {
                const out: Record<string, UserProfileItem[]> = {};
                const forms: Record<string, {gallery: GalleryOption | null, file: File | null}> = {};
                if (val && typeof val === 'object') {
                    for (const k of Object.keys(val)) {
                        out[k] = Array.isArray(val[k]) ? [...val[k]] : [];
                        forms[k] = this.addItemForms[k] || { gallery: null, file: null };
                    }
                }
                this.groupedLocal = out;
                this.addItemForms = forms;
            },
        },

        addItemForms: {
            deep: true,
            handler(forms: Record<string, {gallery: GalleryOption | null, file: File | null}>) {
                if (this.isProcessingGalleryAdd) return;
                
                Object.keys(forms).forEach(groupName => {
                    if(forms[groupName] && forms[groupName].gallery !== null) {
                        this.isProcessingGalleryAdd = true;
                        const gallery = forms[groupName].gallery;
                        addGallery(gallery!.id, groupName, () => {
                            this.loadProfileItems().then(() => {
                                this.addItemForms[groupName] = { gallery: null, file: null };
                                this.isProcessingGalleryAdd = false;
                            }).catch(() => {
                                this.isProcessingGalleryAdd = false;
                            });
                        }, () => {
                            this.isProcessingGalleryAdd = false;
                        });
                    }
                });
            }
        }
    },

    methods: {
        ...mapActions({
            loadProfileItems: "profile/loadProfileItems",
            sortItems: "profile/sortItems",
            sortGroupedItems: "profile/sortGroupedItems",
            deleteProfileItem: "profile/deleteItem",

            addGroupAction: 'profile/addGroup',
            renameGroupAction: 'profile/renameGroup',

            successAlert: "alerts/success",
            errorAlert: "alerts/error",

            loadGalleriesList: "galleries/loadGalleriesList",
        }),

        ...validationMethods(),

        fetchGalleries() {
            this.loadGalleriesList()
                .catch(({data}) => {
                    if(data.message) {
                        this.errorAlert({
                            message: this.$t('profile.galleries_fetch_error'),
                            timeout: 30
                        })
                    }
                });
        },

        removeItem(item: UserProfileItem) {
            this.deleteProfileItem({item})
                .then(() => {
                    this.loadProfileItems();
                })
                .catch(({data}) => {
                    if(data.message) {
                        this.errorAlert({
                            message: data.message,
                            timeout: 30
                        })
                    } else {
                        this.errorAlert({
                            message: this.$t('profile.deleting_error'),
                            timeout: 30
                        })
                    }
                });
        },

        onDragEnd() {
            const flattened: UserProfileItem[] = [];
            for (const name of this.groupNames) {
                flattened.push(...(this.groupedLocal[name] || []));
            }
            this.sortGroupedItems({ groups: this.groupedLocal });
            console.log('onDragEnd', flattened, this.groupedLocal);
        },

        onCategoryDragEnd() {
            this.sortGroupedItems({ groups: this.groupedLocal });
            console.log('onCategoryDragEnd', this.groupedLocal);
        },

        publicUrl(url: string): string {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },

        beginAddGroup() {
            this.isAdding = true;
            this.newGroupName = '';
        },
        cancelAddGroup() {
            this.isAdding = false;
            this.newGroupName = '';
        },
        async commitAddGroup(value: string) {
            console.log('commitAddGroup', value);
            this.newGroupName = value || this.newGroupName;
            const name = (this.newGroupName || '').trim();
            if (!name) return;
            if (this.groupNames.includes(name)) {
                this.errorAlert?.({ message: this.$t('profile.portfolio_group_already_exists'), timeout: 15 });
                return;
            }
            try {
                await this.addGroupAction({ name });
                this.isAdding = false;
                this.newGroupName = '';
                this.successAlert?.({ message: this.$t('profile.portfolio_group_added'), timeout: 8 });
            } catch (e: any) {
                this.errorAlert?.({ message: e?.data?.message || this.$t('profile.portfolio_unknown_error'), timeout: 15 });
            }
        },

        startRename(oldName: string) {
            this.editing[oldName] = true;
            this.editingValue = oldName;
        },
        cancelRename(oldName: string) {
            this.editing[oldName] = false;
            this.editingValue = '';
        },
        async applyRename(newName: string) {
            const oldName = this.editingValue;
            const to = (newName || '').trim();
            if (!to || to === oldName) {
                this.cancelRename(oldName);
                return;
            }
            if (this.groupNames.includes(to)) {
                this.errorAlert?.({ message: this.$t('profile.portfolio_group_already_exists'), timeout: 15 });
                return;
            }
            try {
                await this.renameGroupAction({ from: oldName, to });
                this.successAlert?.({ message: this.$t('profile.portfolio_group_updated'), timeout: 8 });

                this.sortGroupedItems({ groups: this.groupedLocal });
            } catch (e: any) {
                this.errorAlert?.({ message: e?.data?.message || this.$t('profile.portfolio_group_update_error'), timeout: 15 });
            } finally {
                this.cancelRename(oldName);
            }
        },
    }
});
</script>